import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  PanelTop,
  Home as HomeIcon,
  Zap,
  Wrench,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { SERVICES, SITE } from "@/lib/site";

const ICONS = { PanelTop, Home: HomeIcon, Zap, Wrench };

export default function Diensten() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="diensten-page">
      {/* HERO */}
      <section className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-3">Diensten</p>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-[#0A1128] max-w-4xl leading-[1.05]">
            Van storing tot slim huis — wij regelen het.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Vier specialisaties, alle uitgevoerd volgens de nieuwste NEN 1010 richtlijnen. Vakkundig, veilig
            en netjes afgewerkt.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-slate-200 text-[#0A1128] hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
                data-testid={`jump-${s.slug}`}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon] || Sparkles;
          const reverse = i % 2 === 1;
          return (
            <section
              id={s.slug}
              key={s.slug}
              data-testid={`service-${s.slug}`}
              className="py-20 md:py-24 border-b border-slate-200 last:border-none scroll-mt-24"
            >
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <div>
                  <div className="inline-flex items-center gap-3 mb-5">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-[#0A1128] text-[#FF6B00]">
                      <Icon className="w-5 h-5" />
                    </span>
                    <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF6B00]">
                      0{i + 1} — Vakgebied
                    </p>
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A1128] leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.short}</p>
                  <ul className="mt-8 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6B00] text-white font-bold hover:bg-[#E66000] transition-colors"
                      data-testid={`${s.slug}-cta-quote`}
                    >
                      Vraag offerte aan <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                      href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Ik heb een vraag over " + s.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#0A1128] text-[#0A1128] font-bold hover:bg-[#0A1128] hover:text-white transition-colors"
                      data-testid={`${s.slug}-cta-whatsapp`}
                    >
                      Stel je vraag
                    </a>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 shadow-xl">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="hidden md:block absolute -bottom-6 -right-6 bg-[#0A1128] text-white p-6 rounded-2xl shadow-2xl max-w-[240px]">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF6B00] mb-1">
                      Werkgebied
                    </div>
                    <div className="font-display font-bold">{SITE.region}</div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
